import {ChangeDetectionStrategy, Component, computed, inject, input} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import * as _ from "lodash";
import {TaskPurposeType} from "../../../types/task-purpose.type";
import {BringozDatePipe} from "../../pipes/bringoz-date.pipe";

const TASK_PURPOSE_VALUES: string[] = _.values(TaskPurposeType);

const DATE_FIELDS = new Set([
  "notBefore", "notAfter", "startTime", "endTime", "startRecurring", "endRecurring",
  "pickupTask_notBefore", "pickupTask_notAfter", "destinationTask_notBefore", "destinationTask_notAfter",
]);

export type TimelineEvent = Record<string, string>;

export interface TimelineLog {
  level?: string;
  event?: string;
  source?: string;
  createdAt?: number;
  createdBy?: {name?: string; holderType?: string};
  templateData?: TimelineEvent[];
}

@Component({
  selector: "app-bringoz-timeline-logs-table-row",
  templateUrl: "./bringoz-timeline-logs-table-row.html",
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {class: "tr"},
  providers: [BringozDatePipe],
})
export class BringozTimelineLogsTableRowComponent {
  readonly log = input.required<TimelineLog>();

  private readonly translate = inject(TranslateService);
  private readonly datePipe = inject(BringozDatePipe);

  readonly events = computed(() => (this.log().templateData ?? []).map((event) => this.toDisplay(event)));

  readonly eventKey = computed(() => `TIMELINE_LOG_EVENTS.${this.log().level}.${this.log().event}`);

  private toDisplay(event: TimelineEvent): TimelineEvent {
    const display: TimelineEvent = {};
    _.forOwn(event, (value, key) => {
      display[key] = key === "subject" ? this.translateSubject(value) : this.translate.instant(value);
    });
    if (!display.oldValue) {
      display.oldValue = this.translate.instant("None");
    }
    if (!display.newValue) {
      display.newValue = this.translate.instant("None");
    }
    if (DATE_FIELDS.has(display.fieldName)) {
      display.oldValue = this.datePipe.transform({oldValue: parseInt(display.oldValue)}, "oldValue", true);
      display.newValue = this.datePipe.transform({newValue: parseInt(display.newValue)}, "newValue", true);
    }
    if (display.fieldName === "serviceIds") {
      const oldIds = _.split(display.oldValue.replace(/[\[\]]/g, ""), ", ");
      const newIds = _.split(display.newValue.replace(/[\[\]]/g, ""), ", ");
      const added = _.difference(newIds, oldIds);
      const removed = _.difference(oldIds, newIds);
      [added, removed].forEach((ids) => {
        if (ids.length) {
          display.newValue = _.join(ids, ", ");
          display.subject = this.translate.instant(ids.length > 1 ? "Services" : "Service");
        }
      });
    }
    display.fieldName = this.translate.instant("TIMELINE_LOG_FIELD_NAME." + event.fieldName);
    return display;
  }

  private translateSubject(subject: string): string {
    if (!_.isString(subject) || _.isEmpty(subject)) {
      return subject;
    }
    const purposeKey = TASK_PURPOSE_VALUES.find((key) => _.toLower(key) === _.toLower(subject));
    return this.translate.instant(purposeKey ? "TASK_PURPOSE." + purposeKey : subject);
  }
}
